import React, { useState, useMemo, useCallback } from 'react';
import { Lead, User } from '@/lib/api';
import { cn, formatCurrency } from '@/lib/utils';
import { EditableCell } from './EditableCell';
import { BulkActionToolbar } from './BulkActionToolbar';
import {
  ArrowUp,
  ArrowDown,
  Edit,
  Trash2,
  Mail,
  Phone,
  Building,
  User as UserIcon,
} from 'lucide-react';
// Virtual scrolling temporarily disabled due to import issues
// import { FixedSizeList } from 'react-window';

interface LeadsListViewProps {
  leads: Lead[];
  allUsers: User[];
  onLeadUpdate: (leadId: string, data: Partial<Lead>) => Promise<void>;
  onLeadDelete: (leadId: string) => void;
  onLeadClick: (lead: Lead) => void;
  onBulkAssign: (leadIds: string[], userId: string) => Promise<void>;
  onBulkChangeStage: (leadIds: string[], stage: Lead['stage']) => Promise<void>;
  onBulkChangePriority: (leadIds: string[], priority: Lead['priority']) => Promise<void>;
  onBulkDelete: (leadIds: string[]) => Promise<void>;
}

type SortField = 'name' | 'company' | 'stage' | 'priority' | 'dealValue' | 'expectedClose' | 'daysInPipeline' | 'daysInStage' | 'lastActivity' | 'createdAt';
type SortOrder = 'asc' | 'desc';

const STAGE_OPTIONS = [
  { value: 'NEW', label: 'New' },
  { value: 'CONTACTED', label: 'Contacted' },
  { value: 'DISCUSSION', label: 'Discussion' },
  { value: 'PROPOSAL', label: 'Proposal' },
  { value: 'NEGOTIATION', label: 'Negotiation' },
  { value: 'WON', label: 'Won' },
  { value: 'LOST', label: 'Lost' },
];

const PRIORITY_OPTIONS = [
  { value: 'HOT', label: 'Hot' },
  { value: 'WARM', label: 'Warm' },
  { value: 'COLD', label: 'Cold' },
];

export const LeadsListView: React.FC<LeadsListViewProps> = ({
  leads,
  allUsers,
  onLeadUpdate,
  onLeadDelete,
  onLeadClick,
  onBulkAssign,
  onBulkChangeStage,
  onBulkChangePriority,
  onBulkDelete,
}) => {
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Calculate days in pipeline and stage
  const enrichedLeads = useMemo(() => {
    return leads.map(lead => {
      const createdDate = new Date(lead.createdAt);
      const stageDate = new Date(lead.stageUpdatedAt);
      const now = new Date();

      const daysInPipeline = Math.floor((now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
      const daysInStage = Math.floor((now.getTime() - stageDate.getTime()) / (1000 * 60 * 60 * 24));

      return {
        ...lead,
        daysInPipeline,
        daysInStage,
      };
    });
  }, [leads]);

  // Sorting logic
  const sortedLeads = useMemo(() => {
    const sorted = [...enrichedLeads].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'company':
          aValue = a.company?.toLowerCase() || '';
          bValue = b.company?.toLowerCase() || '';
          break;
        case 'stage':
          aValue = STAGE_OPTIONS.findIndex(s => s.value === a.stage);
          bValue = STAGE_OPTIONS.findIndex(s => s.value === b.stage);
          break;
        case 'priority':
          const priorityOrder = { HOT: 0, WARM: 1, COLD: 2 };
          aValue = priorityOrder[a.priority];
          bValue = priorityOrder[b.priority];
          break;
        case 'dealValue':
          aValue = a.dealValue || 0;
          bValue = b.dealValue || 0;
          break;
        case 'expectedClose':
          aValue = a.expectedClose ? new Date(a.expectedClose).getTime() : 0;
          bValue = b.expectedClose ? new Date(b.expectedClose).getTime() : 0;
          break;
        case 'daysInPipeline':
          aValue = a.daysInPipeline || 0;
          bValue = b.daysInPipeline || 0;
          break;
        case 'daysInStage':
          aValue = a.daysInStage || 0;
          bValue = b.daysInStage || 0;
          break;
        case 'lastActivity':
          aValue = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
          bValue = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt).getTime();
          bValue = new Date(b.createdAt).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [enrichedLeads, sortField, sortOrder]);

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedLeadIds.size === sortedLeads.length) {
      setSelectedLeadIds(new Set());
    } else {
      setSelectedLeadIds(new Set(sortedLeads.map(lead => lead.id)));
    }
  };

  const toggleSelectLead = (leadId: string) => {
    const newSelection = new Set(selectedLeadIds);
    if (newSelection.has(leadId)) {
      newSelection.delete(leadId);
    } else {
      newSelection.add(leadId);
    }
    setSelectedLeadIds(newSelection);
  };

  const clearSelection = () => {
    setSelectedLeadIds(new Set());
  };

  // Sort handler
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Cell save handler
  const handleCellSave = async (leadId: string, field: keyof Lead, value: any) => {
    await onLeadUpdate(leadId, { [field]: value });
  };

  // Get badge styles
  const getStageBadgeClass = (stage: string) => {
    const stageClasses: Record<string, string> = {
      NEW: 'bg-blue-100 text-blue-800',
      CONTACTED: 'bg-purple-100 text-purple-800',
      DISCUSSION: 'bg-orange-100 text-orange-800',
      PROPOSAL: 'bg-yellow-100 text-yellow-800',
      NEGOTIATION: 'bg-pink-100 text-pink-800',
      WON: 'bg-green-100 text-green-800',
      LOST: 'bg-red-100 text-red-800',
    };
    return stageClasses[stage] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadgeClass = (priority: string) => {
    const priorityClasses: Record<string, string> = {
      HOT: 'bg-red-100 text-red-800',
      WARM: 'bg-yellow-100 text-yellow-800',
      COLD: 'bg-blue-100 text-blue-800',
    };
    return priorityClasses[priority] || 'bg-gray-100 text-gray-800';
  };

  // Format date
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Table header component
  const TableHeader: React.FC<{ field: SortField; label: string; className?: string }> = ({
    field,
    label,
    className,
  }) => (
    <button
      onClick={() => handleSort(field)}
      className={cn(
        'flex items-center gap-1 px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider hover:bg-gray-100 transition-colors',
        className
      )}
    >
      {label}
      {sortField === field && (
        sortOrder === 'asc' ? (
          <ArrowUp className="w-3 h-3" />
        ) : (
          <ArrowDown className="w-3 h-3" />
        )
      )}
    </button>
  );

  // Row component
  const renderRow = (lead: Lead, index: number) => {
    const isSelected = selectedLeadIds.has(lead.id);

    return (
      <div
        key={lead.id}
        className={cn(
          'flex items-center border-b border-gray-200 hover:bg-gray-50 transition-colors',
          isSelected && 'bg-blue-50',
          index % 2 === 0 && !isSelected && 'bg-white',
          index % 2 === 1 && !isSelected && 'bg-gray-50'
        )}
      >
        {/* Checkbox */}
        <div className="w-12 flex items-center justify-center flex-shrink-0">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleSelectLead(lead.id)}
            className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Lead Name */}
        <div className="w-48 px-3 py-2 flex-shrink-0">
          <button
            onClick={() => onLeadClick(lead)}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left truncate w-full"
          >
            {lead.name}
          </button>
        </div>

        {/* Company */}
        <div className="w-48 px-3 py-2 flex-shrink-0">
          <div className="flex items-center gap-2 text-sm text-gray-900">
            <Building className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <span className="truncate">{lead.company}</span>
          </div>
        </div>

        {/* Contact Person */}
        <div className="w-40 px-3 py-2 flex-shrink-0">
          <span className="text-sm text-gray-700 truncate block">{lead.contactPerson || '-'}</span>
        </div>

        {/* Phone */}
        <div className="w-40 px-3 py-2 flex-shrink-0">
          {lead.phoneNumber && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="truncate">{lead.phoneNumber}</span>
            </div>
          )}
          {!lead.phoneNumber && <span className="text-sm text-gray-400">-</span>}
        </div>

        {/* Email */}
        <div className="w-48 px-3 py-2 flex-shrink-0">
          {lead.email && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="truncate">{lead.email}</span>
            </div>
          )}
          {!lead.email && <span className="text-sm text-gray-400">-</span>}
        </div>

        {/* Stage (Editable) */}
        <div className="w-40 flex-shrink-0">
          <EditableCell
            value={lead.stage}
            leadId={lead.id}
            field="stage"
            onSave={handleCellSave}
            type="select"
            options={STAGE_OPTIONS}
            displayValue={
              <span className={cn('px-2 py-1 rounded-full text-xs font-medium', getStageBadgeClass(lead.stage))}>
                {lead.stage}
              </span>
            }
          />
        </div>

        {/* Priority (Editable) */}
        <div className="w-32 flex-shrink-0">
          <EditableCell
            value={lead.priority}
            leadId={lead.id}
            field="priority"
            onSave={handleCellSave}
            type="select"
            options={PRIORITY_OPTIONS}
            displayValue={
              <span className={cn('px-2 py-1 rounded-full text-xs font-medium', getPriorityBadgeClass(lead.priority))}>
                {lead.priority}
              </span>
            }
          />
        </div>

        {/* Deal Value (Editable) */}
        <div className="w-36 flex-shrink-0">
          <EditableCell
            value={lead.dealValue || null}
            leadId={lead.id}
            field="dealValue"
            onSave={handleCellSave}
            type="number"
            displayValue={lead.dealValue ? formatCurrency(lead.dealValue) : '-'}
          />
        </div>

        {/* Expected Close (Editable) */}
        <div className="w-40 flex-shrink-0">
          <EditableCell
            value={lead.expectedClose || null}
            leadId={lead.id}
            field="expectedClose"
            onSave={handleCellSave}
            type="date"
            displayValue={formatDate(lead.expectedClose)}
          />
        </div>

        {/* Assigned To */}
        <div className="w-44 px-3 py-2 flex-shrink-0">
          {lead.assignedToUser && (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <UserIcon className="w-3 h-3 text-gray-400 flex-shrink-0" />
              <span className="truncate">{lead.assignedToUser.name}</span>
            </div>
          )}
          {!lead.assignedToUser && <span className="text-sm text-gray-400">-</span>}
        </div>

        {/* Days in Pipeline */}
        <div className="w-32 px-3 py-2 flex-shrink-0 text-center">
          <span className="text-sm text-gray-700">{lead.daysInPipeline || 0}</span>
        </div>

        {/* Days in Stage */}
        <div className="w-32 px-3 py-2 flex-shrink-0 text-center">
          <span className="text-sm text-gray-700">{lead.daysInStage || 0}</span>
        </div>

        {/* Last Contact */}
        <div className="w-36 px-3 py-2 flex-shrink-0">
          <span className="text-sm text-gray-700">{formatDate(lead.lastActivity)}</span>
        </div>

        {/* Actions */}
        <div className="w-32 px-3 py-2 flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => onLeadClick(lead)}
              className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
              title="Edit lead"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onLeadDelete(lead.id)}
              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Delete lead"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  const selectedLeads = sortedLeads.filter(lead => selectedLeadIds.has(lead.id));

  return (
    <div className="flex flex-col h-full">
      {/* Table container with horizontal scroll */}
      <div className="flex-1 overflow-hidden bg-white rounded-lg border border-gray-200 shadow-sm">
        {/* Header - Sticky */}
        <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 shadow-sm overflow-x-auto">
          <div className="flex min-w-max">
            {/* Checkbox header */}
            <div className="w-12 flex items-center justify-center flex-shrink-0 px-3 py-3 border-r border-gray-200">
              <input
                type="checkbox"
                checked={sortedLeads.length > 0 && selectedLeadIds.size === sortedLeads.length}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <TableHeader field="name" label="Lead Name" className="w-48 border-r border-gray-200" />
            <TableHeader field="company" label="Company" className="w-48 border-r border-gray-200" />
            <div className="w-40 px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 flex-shrink-0">
              Contact Person
            </div>
            <div className="w-40 px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 flex-shrink-0">
              Phone
            </div>
            <div className="w-48 px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 flex-shrink-0">
              Email
            </div>
            <TableHeader field="stage" label="Stage" className="w-40 border-r border-gray-200" />
            <TableHeader field="priority" label="Priority" className="w-32 border-r border-gray-200" />
            <TableHeader field="dealValue" label="Deal Value" className="w-36 border-r border-gray-200" />
            <TableHeader field="expectedClose" label="Expected Close" className="w-40 border-r border-gray-200" />
            <div className="w-44 px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-r border-gray-200 flex-shrink-0">
              Assigned To
            </div>
            <TableHeader field="daysInPipeline" label="Days in Pipeline" className="w-32 text-center border-r border-gray-200" />
            <TableHeader field="daysInStage" label="Days in Stage" className="w-32 text-center border-r border-gray-200" />
            <TableHeader field="lastActivity" label="Last Contact" className="w-36 border-r border-gray-200" />
            <div className="w-32 px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider flex-shrink-0">
              Actions
            </div>
          </div>
        </div>

        {/* Body - Rendered rows */}
        <div className="overflow-auto" style={{ height: 'calc(100vh - 320px)' }}>
          <div className="min-w-max">
            {sortedLeads.map((lead, index) => renderRow(lead, index))}
          </div>
        </div>

        {/* Empty state */}
        {sortedLeads.length === 0 && (
          <div className="text-center py-16">
            <Building className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium mb-1">No leads found</p>
            <p className="text-gray-400 text-sm">Try adjusting your filters or search terms to find leads.</p>
          </div>
        )}
      </div>

      {/* Bulk action toolbar */}
      <BulkActionToolbar
        selectedLeads={selectedLeads}
        allUsers={allUsers}
        onClearSelection={clearSelection}
        onBulkAssign={onBulkAssign}
        onBulkChangeStage={onBulkChangeStage}
        onBulkChangePriority={onBulkChangePriority}
        onBulkDelete={onBulkDelete}
      />
    </div>
  );
};
